import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { isClickedOutside, onAnimationEnd } from '@theme/utilities';

/** @type {Window & { exponea?: any, webxpClient?: any, webxpClientInitialConfig?: any }} */
const win = /** @type {any} */ (window);

const SUBSCRIBED_EVENT = 'apa:bis:subscribed';

/** Bloomreach's unauthenticated, stream-scoped ingest endpoint. */
const BATCH_PATH = '/track/u/v1/batch';

/**
 * @param {string} name
 * @returns {string | null}
 */
function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Target and stream id, read from the app embed at runtime. Which global holds
 * them depends on how far the SDK got — the snippet parks its config on
 * `webxpClient` and the loaded SDK moves it — so try every known location.
 * @returns {{ url: string, streamId: string } | null}
 */
function resolveEndpoint() {
  const queue = win.exponea?._;
  const queued = Array.isArray(queue) ? queue.flatMap(([, args]) => (args ? Array.from(args) : [])) : [];

  for (const candidate of [win.webxpClientInitialConfig, win.webxpClient, ...queued]) {
    const target = candidate?.path || candidate?.target;
    const streamId = candidate?.streamId || candidate?.stream_id;

    if (target && streamId) {
      return { url: `${target}${BATCH_PATH}?stream_id=${encodeURIComponent(streamId)}`, streamId };
    }
  }

  return null;
}

/**
 * `email_id` is this project's hard ID — Bloomreach rejects `registered`.
 * The soft IDs are absent for a visitor who declined cookies.
 * @param {string} email
 * @returns {Record<string, string>}
 */
function customerIds(email) {
  /** @type {Record<string, string>} */
  const ids = { email_id: email };

  const cookieId = readCookie('__exponea_etc__');
  if (cookieId) ids.cookie = cookieId;

  // The `cart` cookie carries a `?key=` suffix.
  const cartId = readCookie('cart')?.split('?')[0];
  if (cartId) ids.cart_id = cartId;

  return ids;
}

/**
 * Fires the Bloomreach (Exponea) back-in-stock opt-in, bypassing
 * `exponea.track()`. The app embed leaves `window.exponea` a command-queue stub
 * until analytics *and* marketing *and* sale-of-data consent are granted, so
 * `track()` silently sends nothing — and the stub is always present, so no
 * feature check can detect it. The ingest endpoint below takes the event
 * whatever the visitor's cookie choice.
 *
 * @param {object} options
 * @param {string} options.email
 * @param {string} options.eventName
 * @param {string} options.consentCategory
 * @param {string} options.source
 * @param {string} options.productId
 * @param {string} options.variantId
 * @returns {Promise<boolean>} Whether Bloomreach accepted the event.
 */
async function trackBackInStock({ email, eventName, consentCategory, source, productId, variantId }) {
  const endpoint = resolveEndpoint();

  if (!endpoint) {
    console.warn('[APC] Bloomreach back in stock: no tracking config, opt-in event not sent');
    return false;
  }

  const payload = {
    commands: [
      {
        name: 'customers/events',
        data: {
          company_id: endpoint.streamId,
          customer_ids: customerIds(email),
          type: eventName,
          properties: {
            product_id: productId,
            variant_id: variantId,
            email,
            source,
            consent_list: [
              {
                action: 'accept',
                category: consentCategory,
                valid_until: 'unlimited',
              },
            ],
            location: window.location.href,
            page_title: document.title,
            referrer: document.referrer,
          },
        },
      },
    ],
  };

  try {
    const response = await fetch(endpoint.url, {
      method: 'POST',
      // Matches the app embed's own calls; also avoids a CORS preflight.
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify(payload),
      keepalive: true,
    });

    if (!response.ok) throw new Error(`Bloomreach responded with ${response.status}`);

    return true;
  } catch (error) {
    console.error('[APC] Bloomreach back in stock: could not deliver the opt-in event', error);
    return false;
  }
}

/**
 * Back in stock modal component for Bloomreach integration.
 * Collects an email from anonymous customers and fires the opt-in event.
 *
 * @typedef {object} BackInStockModalRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} formContainer - The form container element.
 * @property {HTMLInputElement} emailInput - The email input.
 * @property {HTMLInputElement} [termsCheckbox] - The terms checkbox.
 * @property {HTMLButtonElement} submitButton - The submit button.
 * @property {HTMLElement} submitText - The submit button text.
 * @property {HTMLElement} loadingIndicator - The loading indicator.
 * @property {HTMLElement} successMessage - The success message container.
 * @property {HTMLElement} errorMessage - The error message element.
 *
 * @extends Component<BackInStockModalRefs>
 */
class BloomreachBackInStockModal extends Component {
  requiredRefs = ['dialog', 'formContainer', 'emailInput', 'submitButton', 'errorMessage'];

  #previousScrollY = 0;
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    // Listen for variant updates to sync variant ID
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @returns {string} */
  get eventName() {
    return this.dataset.eventName || 'back_in_stock';
  }

  /** @returns {string} */
  get consentCategory() {
    return this.dataset.consentCategory || 'back_in_stock';
  }

  /** @returns {string} */
  get source() {
    return this.dataset.source || 'product_page';
  }

  /** @returns {string} */
  get productId() {
    return this.dataset.productId || '';
  }

  /** @returns {string} */
  get variantId() {
    return this.dataset.variantId || '';
  }

  /** @returns {string} */
  get customerEmail() {
    return this.dataset.customerEmail || '';
  }

  /** @returns {string} */
  get errorEmailMessage() {
    return this.dataset.errorEmail || 'Please enter a valid email address';
  }

  /** @returns {string} */
  get errorTermsMessage() {
    return this.dataset.errorTerms || 'Please accept the terms and conditions';
  }

  /** @returns {string} */
  get errorApiMessage() {
    return this.dataset.errorApi || 'Something went wrong. Please try again.';
  }

  /**
   * Subscribes a known (logged-in) customer immediately without opening the dialog.
   * @returns {Promise<boolean>} Whether the subscription was tracked successfully.
   */
  async subscribeKnownCustomer() {
    if (!this.customerEmail) return false;
    return this.#subscribe(this.customerEmail);
  }

  /**
   * Shows the modal dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Store scroll position
    this.#previousScrollY = window.scrollY;

    requestAnimationFrame(() => {
      // Lock scroll
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.#previousScrollY}px`;

      dialog.showModal();

      // Focus the email input
      this.refs.emailInput.focus();

      // Add event listeners
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the modal dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    // Remove event listeners
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, { subtree: false });

    // Unlock scroll
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'auto' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    // Reset form state
    this.#resetForm();
  };

  /**
   * Handles the form submission.
   * @param {SubmitEvent} [event]
   */
  handleSubmit = async (event) => {
    event?.preventDefault();

    const { emailInput, termsCheckbox } = this.refs;

    // Hide any previous errors
    this.#hideError();

    // Validate email
    if (!emailInput.value || !this.#isValidEmail(emailInput.value)) {
      this.#showError(this.errorEmailMessage);
      emailInput.focus();
      return;
    }

    // Validate terms if checkbox exists
    if (termsCheckbox && !termsCheckbox.checked) {
      this.#showError(this.errorTermsMessage);
      return;
    }

    if (await this.#subscribe(emailInput.value)) {
      this.#showSuccess();
    } else {
      this.#showError(this.errorApiMessage);
    }
  };

  /**
   * Fires the Bloomreach opt-in event and notifies trigger buttons.
   * @param {string} email
   * @returns {Promise<boolean>}
   */
  async #subscribe(email) {
    const tracked = await trackBackInStock({
      email,
      eventName: this.eventName,
      consentCategory: this.consentCategory,
      source: this.source,
      productId: this.productId,
      variantId: this.variantId,
    });

    if (tracked) {
      document.dispatchEvent(
        new CustomEvent(SUBSCRIBED_EVENT, { detail: { modalId: this.id, variantId: this.variantId } })
      );
    }

    return tracked;
  }

  /**
   * Validates an email address.
   * @param {string} email
   * @returns {boolean}
   */
  #isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    const { errorMessage } = this.refs;
    if (errorMessage) {
      errorMessage.textContent = message;
      errorMessage.hidden = false;
    }
  }

  #hideError() {
    const { errorMessage } = this.refs;
    if (errorMessage) {
      errorMessage.hidden = true;
      errorMessage.textContent = '';
    }
  }

  #showSuccess() {
    const { formContainer, successMessage } = this.refs;
    if (formContainer) formContainer.hidden = true;
    successMessage.hidden = false;

    // Auto-close after 3 seconds
    setTimeout(() => {
      this.closeDialog();
    }, 3000);
  }

  #resetForm() {
    const { formContainer, successMessage, emailInput, termsCheckbox } = this.refs;

    if (formContainer) formContainer.hidden = false;
    if (emailInput) emailInput.value = '';
    if (successMessage) successMessage.hidden = true;
    this.#hideError();

    if (termsCheckbox) {
      termsCheckbox.checked = false;
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDialog();
    }
  };

  /**
   * Handles variant update events to sync the variant ID.
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const { resource } = event.detail;
    if (resource?.id) {
      this.dataset.variantId = String(resource.id);
    }
  };
}

if (!customElements.get('apc-bloomreach-back-in-stock')) {
  customElements.define('apc-bloomreach-back-in-stock', BloomreachBackInStockModal);
}

/**
 * Back in stock trigger button.
 * For logged-in customers it subscribes immediately via the SDK; for anonymous
 * customers it opens the modal to collect an email.
 *
 * @typedef {object} BackInStockButtonRefs
 * @property {HTMLButtonElement} [button] - The button element.
 *
 * @extends Component<BackInStockButtonRefs>
 */
class BloomreachBackInStockButton extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(SUBSCRIBED_EVENT, this.#onSubscribed, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @returns {string} */
  get modalId() {
    return this.dataset.modalId || '';
  }

  /** @returns {boolean} */
  get isLoggedIn() {
    return this.dataset.loggedIn === 'true';
  }

  /**
   * @returns {BloomreachBackInStockModal | null}
   */
  get modal() {
    return this.modalId
      ? /** @type {BloomreachBackInStockModal | null} */ (document.getElementById(this.modalId))
      : null;
  }

  /**
   * Handles the button click.
   * @param {MouseEvent} event
   */
  async handleClick(event) {
    event.preventDefault();

    const { modal } = this;
    if (!modal) return;

    if (this.isLoggedIn) {
      // Subscribe immediately, no email prompt.
      if (await modal.subscribeKnownCustomer()) {
        this.#markSubscribed();
      }
      return;
    }

    modal.showDialog();
  }

  /**
   * Updates this button to a subscribed state when an opt-in succeeds.
   * @param {Event} event
   */
  #onSubscribed = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail || {};
    if (detail.modalId && detail.modalId !== this.modalId) return;
    this.#markSubscribed();
  };

  #markSubscribed() {
    const button = this.querySelector('button');
    if (!button) return;

    const successLabel = this.dataset.successLabel;
    if (successLabel) {
      const label = button.querySelector('.add-to-cart-button-text span') || button.querySelector('span');
      if (label) label.textContent = successLabel;
    }
    button.setAttribute('disabled', '');
    button.classList.add('is-subscribed');
  }
}

if (!customElements.get('apc-bloomreach-back-in-stock-button')) {
  customElements.define('apc-bloomreach-back-in-stock-button', BloomreachBackInStockButton);
}

export { BloomreachBackInStockModal, BloomreachBackInStockButton };
